import React, { useEffect, useState } from 'react';
import {
  Loader2,
  Copy,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Anchor,
} from 'lucide-react';
import { fetchChainWallet, anchorBatchOnChain } from '../../lib/api';

/**
 * ChainAnchorPanel — admin control surface for the Base Sepolia anchor.
 *
 * Reads `/api/provenance/wallet` to display the wallet address, current
 * testnet ETH balance, and connection status. Polls every 12 s while the
 * panel is open so funding is detected the moment the faucet lands.
 *
 * One button to anchor BATCH-042 (idempotent — the backend caches by
 * content_hash so re-clicks don't re-spend gas). When the anchor lands,
 * the panel displays the resulting tx hash + a link to basescan.
 */

const FAUCETS = [
  { label: 'Coinbase faucet',    href: 'https://www.coinbase.com/faucets/base-ethereum-sepolia-faucet' },
  { label: 'QuickNode faucet',   href: 'https://faucet.quicknode.com/base/sepolia' },
  { label: 'Alchemy faucet',     href: 'https://www.alchemy.com/faucets/base-sepolia' },
];

const POLL_MS = 12000;

const ChainAnchorPanel = () => {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [anchoring, setAnchoring] = useState(false);
  const [anchorResult, setAnchorResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const refresh = async () => {
    try {
      setLoading(true);
      const w = await fetchChainWallet();
      setWallet(w);
      setErr('');
    } catch (e) {
      setErr('Could not read wallet status from backend.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, []);

  const onCopy = async () => {
    if (!wallet?.address) return;
    try {
      await navigator.clipboard.writeText(wallet.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const onAnchor = async () => {
    setAnchoring(true);
    setAnchorResult(null);
    try {
      const res = await anchorBatchOnChain('BATCH-042');
      setAnchorResult(res);
      refresh();
    } catch (e) {
      setAnchorResult({ status: 'error', detail: e?.message || 'Network error' });
    } finally {
      setAnchoring(false);
    }
  };

  const ready = wallet?.ready_to_anchor;
  const balanceEth =
    wallet?.balance_eth !== null && wallet?.balance_eth !== undefined
      ? Number(wallet.balance_eth).toFixed(6)
      : '—';

  return (
    <section data-testid="admin-chain-anchor" className="space-y-8">
      {/* Header */}
      <header>
        <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#90B0BF] mb-2">
          Living Vessel · Stage III · {wallet?.network || 'Base Sepolia'}
        </p>
        <h2 className="font-serif text-3xl md:text-4xl text-[#7C9CB1] font-light leading-tight">
          On-chain Anchor.
        </h2>
        <p className="mt-3 text-[#7C9CB1]/85 font-light max-w-2xl">
          Anchor the canonical SHA-256 of <code className="font-mono">BATCH-042</code>'s
          full provenance bundle as the data field of a 0-value self-transaction.
          Idempotent — re-anchoring the same content returns the cached record.
        </p>
      </header>

      {/* Wallet card */}
      <div className="border border-[#C1D7E2] bg-[#F4F8F9] p-6 max-w-2xl">
        <div className="flex items-center justify-between mb-4">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
            Wallet status
          </p>
          <button
            type="button"
            onClick={refresh}
            data-testid="chain-refresh"
            className="inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-[#7C9CB1] hover:text-[#90B0BF]"
          >
            <RefreshCw size={12} strokeWidth={1.6} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {err && (
          <p className="mb-3 font-mono text-[11px] text-[#9c2a2a]">{err}</p>
        )}

        <ul className="space-y-3 font-mono text-[12px] text-[#7C9CB1]">
          <li className="flex items-center justify-between gap-6">
            <span className="tracking-[0.2em] uppercase text-[#90B0BF]">Address</span>
            <span className="flex items-center gap-2">
              <span data-testid="chain-address" className="text-[12px] break-all">
                {wallet?.address || '—'}
              </span>
              <button
                type="button"
                onClick={onCopy}
                data-testid="chain-copy"
                className="text-[#90B0BF] hover:text-[#7C9CB1]"
                title="Copy address"
              >
                <Copy size={12} strokeWidth={1.6} />
              </button>
              {copied && (
                <span className="text-[10px] tracking-[0.18em] uppercase text-[#7C9CB1]">
                  copied
                </span>
              )}
            </span>
          </li>
          <li className="flex items-baseline justify-between border-b border-[#C1D7E2]/60 pb-2">
            <span className="tracking-[0.2em] uppercase text-[#90B0BF]">Network</span>
            <span>{wallet?.network} · chain id {wallet?.chain_id ?? '—'}</span>
          </li>
          <li className="flex items-baseline justify-between border-b border-[#C1D7E2]/60 pb-2">
            <span className="tracking-[0.2em] uppercase text-[#90B0BF]">RPC</span>
            <span className="text-right text-[11px]">{wallet?.rpc_url || '—'}</span>
          </li>
          <li className="flex items-baseline justify-between border-b border-[#C1D7E2]/60 pb-2">
            <span className="tracking-[0.2em] uppercase text-[#90B0BF]">Connected</span>
            <span data-testid="chain-connected">
              {wallet?.connected ? 'Yes · RPC live' : 'No'}
            </span>
          </li>
          <li className="flex items-baseline justify-between">
            <span className="tracking-[0.2em] uppercase text-[#90B0BF]">Balance</span>
            <span data-testid="chain-balance" className="text-[14px]">
              {balanceEth} ETH
            </span>
          </li>
        </ul>

        {/* Funding state */}
        {!ready && wallet && (
          <div
            data-testid="chain-fund-prompt"
            className="mt-6 border-t border-[#C1D7E2] pt-5"
          >
            <p className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#90B0BF] mb-3">
              <AlertCircle size={13} strokeWidth={1.6} />
              Awaiting testnet funds
            </p>
            <p className="text-[#7C9CB1]/85 font-light text-sm leading-relaxed">
              Send a small amount of Base Sepolia ETH to the address above. This
              page polls every 12 s — the moment funds arrive you can press
              <em>Anchor now</em>.
            </p>
            <ul className="mt-4 flex flex-wrap gap-3">
              {FAUCETS.map((f) => (
                <li key={f.label}>
                  <a
                    href={f.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`chain-faucet-${f.label.split(' ')[0].toLowerCase()}`}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-[#C1D7E2] text-[#7C9CB1] text-[10px] tracking-[0.22em] uppercase hover:bg-[#C1D7E2]/40 transition-colors"
                  >
                    {f.label}
                    <ExternalLink size={11} strokeWidth={1.5} />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Anchor action */}
        <div className="mt-6 border-t border-[#C1D7E2] pt-5">
          <button
            type="button"
            onClick={onAnchor}
            disabled={!ready || anchoring}
            data-testid="chain-anchor-button"
            className={`group inline-flex items-center gap-3 px-6 py-3 text-[11px] tracking-[0.24em] uppercase font-medium transition-colors duration-300 ${
              ready
                ? 'bg-[#7C9CB1] text-[#F4F8F9] hover:bg-[#90B0BF]'
                : 'bg-[#C1D7E2]/60 text-[#7C9CB1]/60 cursor-not-allowed'
            }`}
          >
            {anchoring ? (
              <Loader2 size={14} strokeWidth={1.6} className="animate-spin" />
            ) : (
              <Anchor size={14} strokeWidth={1.6} />
            )}
            {anchoring ? 'Broadcasting…' : 'Anchor BATCH-042 now'}
          </button>
          {!ready && (
            <p className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-[#90B0BF]/80">
              Button activates automatically once funding arrives.
            </p>
          )}
        </div>
      </div>

      {/* Anchor result card */}
      {anchorResult && (
        <div
          data-testid="chain-anchor-result"
          className={`max-w-2xl border p-6 ${
            anchorResult.status === 'success' || anchorResult.status === 'cached'
              ? 'border-[#7C9CB1] bg-[#7C9CB1]/10'
              : 'border-[#90B0BF] bg-[#F4F8F9]'
          }`}
        >
          <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#7C9CB1] mb-3">
            {(anchorResult.status === 'success' || anchorResult.status === 'cached') ? (
              <CheckCircle2 size={14} strokeWidth={1.6} />
            ) : (
              <AlertCircle size={14} strokeWidth={1.6} />
            )}
            {anchorResult.status === 'success'  && 'Anchor broadcast confirmed'}
            {anchorResult.status === 'cached'   && 'Already anchored · cached'}
            {anchorResult.status !== 'success' && anchorResult.status !== 'cached' && (
              <>Status · {String(anchorResult.status).replace(/_/g, ' ')}</>
            )}
          </p>

          {anchorResult.anchor && (
            <ul className="space-y-2 font-mono text-[12px] text-[#7C9CB1]">
              <li className="flex items-baseline justify-between gap-6 border-b border-[#C1D7E2]/60 pb-2">
                <span className="tracking-[0.2em] uppercase text-[#90B0BF]">Tx</span>
                <span className="break-all text-right">{anchorResult.anchor.tx_hash}</span>
              </li>
              <li className="flex items-baseline justify-between gap-6 border-b border-[#C1D7E2]/60 pb-2">
                <span className="tracking-[0.2em] uppercase text-[#90B0BF]">Block</span>
                <span>#{anchorResult.anchor.block_number}</span>
              </li>
              <li className="flex items-baseline justify-between gap-6">
                <span className="tracking-[0.2em] uppercase text-[#90B0BF]">Anchored</span>
                <span>{(anchorResult.anchor.anchored_at || '').slice(0, 19).replace('T', ' ')}</span>
              </li>
            </ul>
          )}

          {anchorResult.detail && (
            <p className="mt-3 text-[#7C9CB1]/85 font-light text-sm leading-relaxed">
              {anchorResult.detail}
            </p>
          )}

          {anchorResult.anchor?.explorer_url && (
            <a
              href={anchorResult.anchor.explorer_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="chain-explorer-link"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-[#7C9CB1] text-[#7C9CB1] text-[10px] tracking-[0.22em] uppercase hover:bg-[#7C9CB1] hover:text-[#F4F8F9] transition-colors"
            >
              Open on basescan
              <ExternalLink size={11} strokeWidth={1.5} />
            </a>
          )}
        </div>
      )}
    </section>
  );
};

export default ChainAnchorPanel;
